/* eslint-disable import/no-extraneous-dependencies */
import React, {useCallback, useMemo} from 'react'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {makeStyles, Switch, TableCell, TableRow} from '@material-ui/core'
import {DragIndicator} from '@material-ui/icons'
import ProductsManagementStyle from 'pages/ProductsPages/ProductsList/Style'
import {ListProductType} from 'api/productApi'
import {useTranslation} from 'react-i18next'
import {usdToCent} from 'helpers/paymentHelper'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  ALL_SALE_TARGET,
  PAYMENT_METHOD,
  PREPAYMENT_ITEM,
  PRODUCT_MANAGEMENT_TABLE_HEADER,
  SALE_STATUS,
  SaleStatus,
  TARGET_TYPE,
} from '../constant'

interface DraggableTableRowProps {
  item: ListProductType
  onClickRow: (goodsId: string) => void
  onChangeSaleStatus: (
    event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
    id: string,
    currentStatus: SaleStatus,
  ) => void
  index: number
  searchParams: any
  productLength: number
  onChangeProductArchiveStatus: (goodsId: string, isArchived: boolean) => void
}

const useStyles = makeStyles(ProductsManagementStyle)

const goodsNameStyle = {
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
}

const goodsDescriptionStyle = {
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
}

const getCellStyle = (key: string) => {
  if (key === 'goodsName') return goodsNameStyle
  if (key === 'goodsDescription') return goodsDescriptionStyle
  return undefined
}

const DraggableTableRow = (props: DraggableTableRowProps) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const {
    item,
    onClickRow,
    onChangeSaleStatus,
    index,
    searchParams,
    productLength,
    onChangeProductArchiveStatus,
  } = props
  const {isEqualSaleTarget} = useProductManagement()
  const {attributes, listeners, transform, transition, setNodeRef, isDragging} =
    useSortable({
      id: item.goods.goodsId,
    })

  const style = useMemo(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
    }
  }, [isDragging, transform, transition])

  const generateCellTable = useCallback(
    (data: ListProductType, key: string, index: number) => {
      const {goodsName, saleStatus} = data.goods
      const {salePrice, saleTarget, paymentMethod} = data.sale
      const {usagePeriod, offerType} = data.offer

      switch (key) {
        case 'goodsId': {
          const page = Number(searchParams.page) || 0
          const size = Number(searchParams.size) || 10
          return page * size + (productLength - index)
        }
        case 'goodsName':
          return i18n.language === 'ko'
            ? goodsName.find((item) => item.langCode === 'ko')?.name
            : goodsName.find((item) => item.langCode === 'en')?.name
        case 'salePrice': {
          const item = salePrice.find((item) =>
            i18n.language === 'ko'
              ? item.currency === 'KRW'
              : item.currency === 'USD',
          )
          return `${item?.currency} ${
            item?.currency === 'USD' ? usdToCent(item?.price) : item?.price
          }`
        }
        case 'targetOp': {
          if (isEqualSaleTarget(saleTarget, ALL_SALE_TARGET)) {
            return '-'
          }
          const orgTargets = saleTarget.targets
            .filter((t) => t.targetType === TARGET_TYPE.ORG_TYPE)
            .map((t) => t.targetId)
          return orgTargets.length > 0 ? t(`I${orgTargets.join(', ')}`) : '-'
        }
        case 'paymentMethod': {
          if (paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
            if (offerType === PREPAYMENT_ITEM.PROVIDER) {
              return t('ISubscription')
            }
            if (offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
              return t('IPrepaid')
            }
          }
          return t(`IManual`)
        }
        case 'targetNation': {
          if (isEqualSaleTarget(saleTarget, ALL_SALE_TARGET)) {
            return '-'
          }
          const regionTargets = saleTarget.targets
            .filter((t) => t.targetType === TARGET_TYPE.REGION)
            .map((t) => t.targetId)
          return regionTargets.length > 0
            ? t(`I${regionTargets.join(', ')}`)
            : '-'
        }
        case 'usagePeriod':
          return usagePeriod > 10000000
            ? t('IUnlimited')
            : `${usagePeriod} ${t('IMonths')}`
        case 'saleStatus':
          if ([SALE_STATUS.READY, SALE_STATUS.OFF_SALE].includes(saleStatus))
            return t('IOffSale')
          if (saleStatus === SALE_STATUS.ON_SALE) return t('IOnSale')
          return '-'
        case 'dragHandle':
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
              {...listeners}
              {...attributes}
            >
              <DragIndicator />
            </div>
          )
        case 'goodsDescription':
          return i18n.language === 'ko'
            ? goodsName.find((item) => item.langCode === 'ko')?.description
            : goodsName.find((item) => item.langCode === 'en')?.description
        case 'inquirySelect':
          return (
            <Switch
              color='primary'
              checked={data.goods.isArchived}
              size='small'
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation()
                onChangeProductArchiveStatus(
                  data.goods.goodsId,
                  !data.goods.isArchived,
                )
              }}
            />
          )
        default:
          return '-'
      }
    },
    [
      isEqualSaleTarget,
      i18n.language,
      productLength,
      searchParams.page,
      searchParams.size,
      t,
    ],
  )

  const cellContents = useMemo(
    () =>
      PRODUCT_MANAGEMENT_TABLE_HEADER.map((cel) => ({
        id: cel.id,
        key: cel.key,
        content: generateCellTable(item, cel.key, index),
      })),
    [item, index, generateCellTable],
  )

  const handleCellClick = useCallback(
    (
      event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
      key: string,
    ) => {
      if (key === 'saleStatus') {
        onChangeSaleStatus(event, item.goods.goodsId, item.goods.saleStatus)
      }
    },
    [item.goods.goodsId, item.goods.saleStatus, onChangeSaleStatus],
  )

  return (
    <TableRow
      key={item.goods.goodsId}
      onClick={() => onClickRow(item.goods.goodsId)}
      className={classes.rowCell}
      ref={setNodeRef}
      style={style}
    >
      {cellContents.map((cel) => (
        <TableCell
          key={cel.id}
          align='center'
          padding='checkbox'
          onClick={(event) => handleCellClick(event, cel.key)}
          className={cel.key === 'displayInfos' ? classes.cellRedirect : ''}
          style={getCellStyle(cel.key)}
        >
          {cel.content}
        </TableCell>
      ))}
    </TableRow>
  )
}

DraggableTableRow.displayName = 'DraggableTableRow'

export default DraggableTableRow
